import { create } from 'zustand';
import { HttpError } from '../services/apiClient';
import { tasksService } from '../services/tasksService';
import type { DeactivateTaskOptions, SnoozeDuration } from '../services/tasksService';
import type { ExpectedRelanceDto, RelanceListItemDto } from '../types/customerFollowUp/relance';
import { TaskCategory } from '../types/enums';
import { useTasksStore } from './tasksStore';

/**
 * Relance section ids on the Home screen. The string values double as the `data-testid`
 * suffix (`home-section-<value>`) consumed by the E2E selectors, so they stay stable.
 */
export enum RelanceTab {
  ARelancer = 'aRelancer',
  Repoussees = 'repoussees',
  Paid = 'encaissees',
}

// Floor a timestamp to the first second of its UTC day (00:00:00Z). Mirrors the backend
// TaskService.StartOfUtcDay: a snoozed relance always re-surfaces at the start of its
// reactivation day, so the optimistic "Revient dans X" countdown matches the persisted value.
function startOfUtcDayIso(ms: number): string {
  const d = new Date(ms);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString();
}

function computeClientSnoozeUntil(duration: SnoozeDuration, customDate?: string | null): string | null {
  const now = Date.now();
  switch (duration) {
    case 'Hours24': return startOfUtcDayIso(now + 24 * 60 * 60 * 1000);
    case 'Days7':   return startOfUtcDayIso(now + 7 * 24 * 60 * 60 * 1000);
    case 'Days14':  return startOfUtcDayIso(now + 14 * 24 * 60 * 60 * 1000);
    case 'Custom':  return customDate ? new Date(`${customDate}T00:00:00Z`).toISOString() : null;
    default:        return null; // Indefinite
  }
}

/**
 * Relance-specific deactivation/reactivation view: the snoozed/ignored and paid lists behind the
 * Home sections. Kept separate from the type-agnostic `tasksStore` (which only knows about the generic
 * daily task list). Reads `tasksStore.dailyTasks` state to build the optimistic deactivated entry,
 * then coordinates through public action entry points — `tasksStore.removeTask(id)` and
 * `fetchDailyTasks()` — without calling into private implementation details.
 */
interface RelanceDeactivationState {
  deactivatedItems: RelanceListItemDto[];
  /** Loaded with the deactivated list, from the same response. */
  expectedItems: ExpectedRelanceDto[];
  deactivatedLoading: boolean;
  deactivatedLoaded: boolean;
  /** Load failures only: a failed action would hide a list that is still valid. */
  error: string | null;
  paidItems: RelanceListItemDto[];
  paidLoading: boolean;
  paidError: string | null;
  paidPage: number;
  paidTotalCount: number;
  paidLoadingMore: boolean;

  fetchDeactivatedItems: () => Promise<void>;
  fetchPaidItems: () => Promise<void>;
  fetchMorePaidItems: () => Promise<void>;
  /** Resolves false on failure; the caller reports it. */
  deactivateTask: (taskId: string, options: DeactivateTaskOptions) => Promise<boolean>;
  /** Like `deactivateTask`; a 409 (already active or closed) counts as done, the lists are reconciled either way. */
  reactivateTask: (taskId: string, reason?: string | null) => Promise<boolean>;
  reset: () => void;
}

export const useRelanceDeactivationStore = create<RelanceDeactivationState>((set, get) => ({
  deactivatedItems: [],
  expectedItems: [],
  deactivatedLoading: false,
  deactivatedLoaded: false,
  error: null,
  paidItems: [],
  paidLoading: false,
  paidError: null,
  paidPage: 1,
  paidTotalCount: 0,
  paidLoadingMore: false,

  fetchDeactivatedItems: async () => {
    set({ deactivatedLoading: true, error: null });
    try {
      const response = await tasksService.getDailyTasks('Deactivated');
      const items = response.sections.flatMap(s =>
        s.type === TaskCategory.CUSTOMER_RELANCE ? s.items : []
      );
      set({
        deactivatedItems: items,
        expectedItems: response.expectedRelances ?? [],
        deactivatedLoading: false,
        deactivatedLoaded: true,
      });
    } catch (e) {
      set({ error: (e as Error).message, deactivatedLoading: false });
    }
  },

  fetchPaidItems: async () => {
    set({ paidLoading: true, paidError: null });
    try {
      const response = await tasksService.getDailyTasks('Paid', 1);
      const section = response.sections.find(s => s.type === TaskCategory.CUSTOMER_RELANCE);
      set({
        paidItems: section?.items ?? [],
        paidTotalCount: section?.totalCount ?? 0,
        paidPage: 1,
        paidLoading: false,
      });
    } catch (e) {
      set({ paidError: (e as Error).message, paidLoading: false });
    }
  },

  fetchMorePaidItems: async () => {
    const { paidItems, paidTotalCount, paidLoadingMore, paidPage } = get();
    if (paidLoadingMore || paidItems.length >= paidTotalCount) return;

    set({ paidLoadingMore: true, paidError: null });
    try {
      const nextPage = paidPage + 1;
      const response = await tasksService.getDailyTasks('Paid', nextPage);
      const section = response.sections.find(s => s.type === TaskCategory.CUSTOMER_RELANCE);
      set({
        paidItems: [...get().paidItems, ...(section?.items ?? [])],
        paidTotalCount: section?.totalCount ?? get().paidTotalCount,
        paidPage: nextPage,
        paidLoadingMore: false,
      });
    } catch (e) {
      set({ paidError: (e as Error).message, paidLoadingMore: false });
    }
  },

  deactivateTask: async (taskId, options) => {
    try {
      await tasksService.deactivateTask(taskId, options);
    } catch {
      return false;
    }

    // A snoozed or ignored relance being snoozed again has no active-list entry to build an
    // optimistic row from, so its row is reloaded to carry the new date and note. A sync may have
    // reactivated it, so it can also sit in the active list: drop it there too.
    if (get().deactivatedItems.some(i => i.id === taskId)) {
      useTasksStore.getState().removeTask(taskId);
      await get().fetchDeactivatedItems();
      return true;
    }

    // Read the raw item from the active list (owned by tasksStore) to build the optimistic
    // deactivated entry, then ask tasksStore to drop it — no reaching into its internals.
    const rawItem = useTasksStore.getState().dailyTasks?.sections
      .flatMap(s => s.type === TaskCategory.CUSTOMER_RELANCE ? s.items : [])
      .find(i => i.id === taskId);

    if (rawItem) {
      set({
        deactivatedItems: [
          ...get().deactivatedItems,
          {
            ...rawItem,
            isActive: false,
            deactivatedAt: new Date().toISOString(),
            snoozeUntil: computeClientSnoozeUntil(options.snoozeDuration, options.customDate),
            deactivationNote: options.note ?? null,
          },
        ],
      });
    }

    useTasksStore.getState().removeTask(taskId);
    return true;
  },

  reactivateTask: async (taskId, reason) => {
    try {
      await tasksService.reactivateTask(taskId, reason);
    } catch (e) {
      if (!(e instanceof HttpError && e.status === 409)) return false;
    }
    // Drop from whichever list held it — reactivation is reachable from both the snoozed/ignored
    // rows and the Paid rows — then refetch the active list so it reappears there.
    // force: true — a background refresh (e.g. Home's post-sync fetch) may already be in flight;
    // awaiting that stale promise would resolve before the reactivation and miss the reactivated item.
    const wasPaid = get().paidItems.some(i => i.id === taskId);
    set({
      deactivatedItems: get().deactivatedItems.filter(i => i.id !== taskId),
      paidItems: get().paidItems.filter(i => i.id !== taskId),
      // Decrement the server-authoritative badge count in place so the UI doesn't require a
      // refetch of the Paid section to reflect the removed row.
      paidTotalCount: wasPaid ? Math.max(0, get().paidTotalCount - 1) : get().paidTotalCount,
    });
    await useTasksStore.getState().fetchDailyTasks({ force: true });
    return true;
  },

  reset: () => set({
    deactivatedItems: [],
    expectedItems: [],
    deactivatedLoading: false,
    deactivatedLoaded: false,
    error: null,
    paidItems: [],
    paidLoading: false,
    paidError: null,
    paidPage: 1,
    paidTotalCount: 0,
    paidLoadingMore: false,
  }),
}));
